import React, {
  useContext,
  useState,
  useEffect,
  useReducer,
} from 'react'
import axios from 'axios';
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import styles from './StudentInfoForm.module.sass'
import { CustomerContext } from '../../contexts/CustomerContext'
import Grid from '../Grid'
import TextInput from '../TextInput'
import DateOfBirthEntry from '../DateOfBirthEntry'
import Button from '../Button'
import StudentInfo from '../StudentIdForm/StudentInfo'
import GradeInput from '../GradeInput'
import { UPDATE_VALUE, initialState, reducer } from './reducer'
import Typography from '../Typography'
import useQueryString from '../../hooks/use-query-string'
import {
  OrderPackage,
  Package,
  School,
  SchoolType,
  Student,
} from '../../models/_models'
import { routes } from '../../routes'
import useLocalStorage from '../../hooks/useLocalStorage'
import AccessCodeOnlyText from '../StudentIdForm/StudentInfo/AccessCodeOnlyText'

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#000' : '#777',
    fontFamily: 'inherit',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#777',
    fontFamily: 'inherit',
    fontSize: '0.95em',
  }),
}

const StudentInfoForm = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { app, o: isOther, p, order } = useQueryString()
  const {
    firstName,
    lastName,
    teacher,
    submissionFail,
    teacherSubmissionFail,
    dateOfBirth,
    grade,
    student,
    errorMessage,
  } = state
  const { customer, setCustomer } = useContext(CustomerContext)
  const history = useHistory()
  const [apptPackage, setApptPackage] = useState()
  const [isHighSchool, setIsHighSchool] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [dobPercentage, setDobPercentage] = useState(0)
  const [dobDataLoaded, setDobDataLoaded] = useState(false)
  const [, setIsUsingAccessCode] = useLocalStorage('isUsingAccessCode')
  const isYearbookOrder = order === 'yearbook_order'
  const accessCodeOnly = customer.currentSchool.accessCodeOnly && !isYearbookOrder

  setIsUsingAccessCode(false)

  useEffect(() => {
    if (!app) return

    if (p) {
      Package
        .find(p)
        .then((res) => setApptPackage(res.data))
    } else {
      Package
        .where({schoolId: customer.currentSchool.id, shouldShowAppointments: true})
        .find()
        .then((res) => setApptPackage(res.data))
    }
  }, [app])

  useEffect(() => {
    if (customer.currentSchool.schoolTypeId) {
      SchoolType
        .find(customer.currentSchool.schoolTypeId)
        .then((res) => {
          setIsHighSchool(res.data.name === 'High School')
        })
    } else {
      School
        .find(customer.currentSchool.id)
        .then((res) => {
          SchoolType
            .find(res.data.schoolTypeId)
            .then((schoolTypeRes) => {
              setIsHighSchool(schoolTypeRes.data.name === 'High School')
            })
        })
    }
  }, [])

  useEffect(() => {
    axios.get(`/students/teachers?school_id=${customer.currentSchool.id}`)
      .then((res) => {
        const newTeachers = res
          .data.data
          .map(({ teacher: resTeacher }) => resTeacher)
          .filter((teacherName) => teacherName && teacherName.length > 0)

        setTeachers([...new Set(newTeachers)])
      })
  }, [])

  useEffect(() => {
    axios.get(`/students/dob_count?school_id=${customer.currentSchool.id}`)
      .then((res) => {
        setDobPercentage(res.data.data.with_dob / res.data.data.all)
        setDobDataLoaded(true)
      })
  }, [])

  const changeHandler = (key, value) => dispatch({ type: UPDATE_VALUE, payload: { key, value } })

  const continueHandler = (e) => {
    e.preventDefault()

    if (isYearbookOrder) {
      history.push(routes.yearbooks({ sid: student.studentId, sch: customer.currentSchool.id }))
    } else if (apptPackage) {
      const orderPackage = new OrderPackage({
        package: apptPackage,
        packageId: apptPackage.id,
        cartId: customer.apptCart.id,
        student,
        studentId: student.id,
      })

      setCustomer({ ...customer, currentStudent: student, currentOrderPackage: orderPackage })
      history.push(routes.appointmentOptionSelection(apptPackage.id))
    } else {
      setCustomer({ ...customer, currentStudent: student })
      history.push('/package_selection')
    }
  }

  const submitHandler = (e) => {
    e.preventDefault()

    if (teacherSubmissionFail || isOther) {
      const params = {
        firstName,
        lastName,
        dob: dateOfBirth,
        schoolId: customer.currentSchool.id,
        teacher,
        grade,
        enrolled: false,
        idOnly: false,
      }

      const createdStudent = new Student(params)

      createdStudent.save()
        .then(() => {
          changeHandler('submissionFail', false)
          changeHandler('student', createdStudent)

          if (isYearbookOrder) {
            history.push(routes.yearbooks({ sid: student.studentId, sch: customer.currentSchool.id }))
          } else {
            const orderPackage = new OrderPackage({
              package: apptPackage,
              packageId: apptPackage.id,
              cartId: customer.apptCart.id,
              student: createdStudent,
              studentId: createdStudent.id,
            })

            orderPackage
              .save()
              .then(() => {
                setCustomer({
                  ...customer,
                  currentStudent: createdStudent,
                  currentOrderPackage: orderPackage,
                })

                if (apptPackage) {
                  history.push(routes.appointmentOptionSelection(apptPackage.id, { o: isOther }))
                } else {
                  history.push('/package_selection')
                }
              })
          }
        })
        .catch(() => changeHandler('submissionFail', true))
    } else if (submissionFail || accessCodeOnly) {
      const params = {
        firstName: { match: firstName },
        lastName: { match: lastName },
        schoolId: customer.currentSchool.id,
        teacher: { match: teacher },
        grade,
        is_enrolled: true,
      }

      Student
        .where(params)
        .all()
        .then((response) => {
          if (response.data.length > 0) {
            changeHandler('teacherSubmissionFail', false)
            changeHandler('errorMessage', '')
            changeHandler('student', response.data[0])
          } else if ((firstName.length > 3) || lastName.length > 3) {
            Student
              .where({
                ...params,
                firstName: {
                  match: firstName.substring(0, 3),
                },
                lastName: {
                  match: lastName.substring(0, 3),
                },
              })
              .all()
              .then((responseTwo) => {
                if (responseTwo.data.length === 1) {
                  changeHandler('teacherSubmissionFail', false)
                  changeHandler('errorMessage', '')
                  changeHandler('student', responseTwo.data[0])
                } else {
                  changeHandler('teacherSubmissionFail', true)
                  changeHandler('errorMessage', 'No image preview is available. A placeholder image will be displayed to allow you to select a background.')
                }
              })
          } else {
            changeHandler('teacherSubmissionFail', true)
            changeHandler('errorMessage', 'No image preview is available. A placeholder image will be displayed to allow you to select a background.')
          }
        })
        .catch(() => changeHandler('teacherSubmissionFail', true))
    } else if (isYearbookOrder) {
      const params = {
        firstName: { match: firstName },
        lastName: { match: lastName },
        dob: { eql: dateOfBirth },
        schoolId: customer.currentSchool.id,
        is_enrolled: true,
        grade,
      }

      Student
        .where(params)
        .all()
        .then((response) => {
          if (response.data.length > 0) {
            history.push(routes.yearbooks({ sid: response.data.studentId, sch: customer.currentSchool.id }))
          } else {
            const createdStudent = new Student({ ...params, firstName, lastName })

            createdStudent
              .save()
              .then(() => {
                history.push(routes.yearbooks({ sid: createdStudent.studentId, sch: customer.currentSchool.id }))
              })
          }
        })
        .catch(() => changeHandler('submissionFail', true))
    } else if (firstName.length > 3 || lastName.length > 3) {
      const params = {
        firstName: { match: firstName },
        lastName: { match: lastName },
        dob: { eql: dateOfBirth },
        schoolId: customer.currentSchool.id,
        is_enrolled: true,
      }

      Student
        .where(params)
        .all()
        .then((response) => {
          if (response.data.length > 0) {
            changeHandler('submissionFail', false)
            changeHandler('student', response.data[0])
          } else {
            changeHandler('submissionFail', true)
          }
        })
        .catch(() => changeHandler('submissionFail', true))
    } else {
      changeHandler('submissionFail', true)
    }
  }

  const labelPrefix = isOther || isYearbookOrder ? '' : "Student's "

  return (
    <form onSubmit={student ? continueHandler : submitHandler} className={styles.form}>
      <Grid container justify="center" spacing={3}>
        {submissionFail && (
          <Grid item xs={11}>
            <Typography align="center" classes={styles.errorText}>{errorMessage}</Typography>
          </Grid>
        )}
        <Grid item xs={11}>
          <TextInput
            required
            fullWidth
            val={firstName}
            label={`${labelPrefix}First Name`}
            onChange={(e) => changeHandler('firstName', e.target.value)}
          />
        </Grid>
        <Grid item xs={11}>
          <TextInput
            required
            fullWidth
            val={lastName}
            label={`${labelPrefix}Last Name`}
            onChange={(e) => changeHandler('lastName', e.target.value)}
          />
        </Grid>
        {dobDataLoaded && dobPercentage > 0.5 && (
          <>
            {!submissionFail && (
              <Grid item xs={11}>
                <DateOfBirthEntry onChange={(value) => changeHandler('dateOfBirth', value)} />
              </Grid>
            )}
          </>
        )}
        {(submissionFail || accessCodeOnly || isYearbookOrder) && (
          <>
            <Grid item xs={11}>
              <label style={{ fontSize: '0.8em', marginLeft: '14px' }}>Grade</label>
              <GradeInput
                required
                val={grade}
                onChange={(val) => {
                  changeHandler('grade', val)
                }}
              />
            </Grid>
            {!isHighSchool && (submissionFail || grade !== 'STAFF') && (
              <>
                {teachers.length > 0 ? (
                  <Grid item xs={11}>
                    <label style={{ fontSize: '0.8em', marginLeft: '14px' }}>Teacher's Name</label>
                    <Select
                      defaultValue={teachers[0]}
                      styles={customStyles}
                      options={teachers
                        .map((teacherName) => ({ value: teacherName, label: teacherName }))}
                      onChange={(e) => changeHandler('teacher', e.value)}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={11}>
                    <TextInput
                      required
                      fullWidth
                      val={teacher}
                      label="Teacher's Name"
                      onChange={(e) => changeHandler('teacher', e.target.value)}
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        )}
        {student && (order === 'yearbook_order' || (!isOther && !accessCodeOnly)) && <StudentInfo student={student} accessCodeOnly={accessCodeOnly} />}
        <Grid item xs={11}>
          {student || teacherSubmissionFail ? (
            <>
              {accessCodeOnly ? (
                <AccessCodeOnlyText byName />
              ) : (
                <Button variant="submit" fullWidth>Continue</Button>
              )}
            </>
          ) : (
            <Button variant="primary" fullWidth>Search</Button>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

export default StudentInfoForm
